<script setup>
import {Link, useForm} from "@inertiajs/vue3";
import LogoSvg from '@svg/new-logo.svg'
import SocialXSvg from '@svg/new-social-x.svg'
import SocialFacebookSvg from '@svg/new-social-facebook.svg'
import SocialYouTubeSvg from '@svg/new-social-youtube.svg'
import SocialBlueskySvg from '@svg/bluesky.svg'

const newsletterForm = useForm({
    email: null
})
</script>

<template>
    <div class="bg-gradient-to-b from-[rgba(19,38,111,0)] to-[rgba(19,38,111,0.6)] mt-16 md:mt-36">
        <footer class="xl:mt-24 max-w-7xl mx-auto px-6">
            <div class="flex items-center justify-between border-b border-b-[rgba(112,120,176,0.3)] py-5">
                <div></div>
                <div class="space-x-1 md:space-x-3 flex items-center lg:-mb-2">
                    <a href="https://x.com/teamcodecourse" target="_blank" class="p-1" aria-label="X (Twitter)">
                        <SocialXSvg class="h-5 w-5 fill-[rgba(233,232,255,1)]" />
                    </a>
                    <a href="https://bsky.app/profile/codecourse.com" target="_blank" class="p-1" aria-label="X (Twitter)">
                        <SocialBlueskySvg class="h-5 w-5 fill-[rgba(233,232,255,1)]" />
                    </a>
                    <a href="https://www.facebook.com/teamcodecourse" target="_blank" class="p-1" aria-label="Facebook">
                        <SocialFacebookSvg class="h-5 w-5 fill-[rgba(233,232,255,1)]" />
                    </a>
                    <a href="https://www.youtube.com/codecourse" target="_blank" class="p-1" aria-label="YouTube">
                        <SocialYouTubeSvg class="size-7 fill-[rgba(233,232,255,1)]" />
                    </a>
                </div>
            </div>
            <div class="flex flex-wrap justify-between border-b border-b-[rgba(112,120,176,0.3)] space-y-6 lg:space-y-0 space-x-0 lg:space-x-6">
                <div class="grow max-w-sm mt-8">
                    <div class="text-xl font-semibold text-[rgba(233,232,255,1)]">Join the newsletter</div>
                    <form v-on:submit.prevent="newsletterForm.post(`/newsletter`, { preserveScroll: true, onSuccess: () => newsletterForm.reset() })" class="mt-5 flex items-center space-x-3">
                        <input type="email" class="bg-[rgba(14,20,53,1)] text-[rgba(233,232,255,1)] border border-[rgba(112,120,176,0.3)] rounded-xl placeholder:text-[rgba(148,162,209,1)] text-sm h-10 w-full px-4" placeholder="Your email address" required v-model="newsletterForm.email">
                        <button class="bg-[rgba(0,255,255,1)] text-[rgba(7,3,27,1)] h-9 px-4 rounded-xl text-sm font-medium drop-shadow-[2px_6px_6px_rgba(0,255,255,0.2)]">Join</button>
                    </form>
                    <p class="text-[rgba(112,120,176,1)] text-xs font-light mt-2">No spam, just occasional updates.</p>
                </div>
                <div class="grid grid-cols-2 md:grid-cols-3 gap-12 md:gap-16 w-full lg:w-auto py-8">
                    <div class="space-y-4">
                        <h5 class="text-[rgba(148,162,209,1)] text-sm font-medium !mb-5 leading-none">Learn</h5>

                        <Link :href="`/library`" class="block text-[rgba(233,232,255,1)] leading-none font-medium whitespace-nowrap">
                            Courses
                        </Link>
                        <Link :href="`/paths`" class="block text-[rgba(233,232,255,1)] leading-none font-medium whitespace-nowrap">
                            Paths
                        </Link>
                        <Link :href="`/snippets`" class="block text-[rgba(233,232,255,1)] leading-none font-medium whitespace-nowrap">
                            Snippets
                        </Link>
                        <Link :href="`/topics`" class="block text-[rgba(233,232,255,1)] leading-none font-medium whitespace-nowrap">
                            Topics
                        </Link>
                        <Link :href="`/articles`" class="block text-[rgba(233,232,255,1)] leading-none font-medium whitespace-nowrap">
                            Articles
                        </Link>
                        <Link :href="`/search`" preserve-scroll class="block text-[rgba(233,232,255,1)] leading-none font-medium whitespace-nowrap">
                            Search
                        </Link>
                    </div>
                    <div class="space-y-4">
                        <h5 class="text-[rgba(148,162,209,1)] text-sm font-medium !mb-5 leading-none">Get help</h5>

                        <Link :href="`/forum`" class="block text-[rgba(233,232,255,1)] leading-none font-medium whitespace-nowrap">
                            Forum
                        </Link>
                        <Link :href="`/contact`" :data="{ from: $page.url }" preserve-scroll class="block text-[rgba(233,232,255,1)] leading-none font-medium whitespace-nowrap">
                            Contact us
                        </Link>
                    </div>
                    <div class="space-y-4">
                        <h5 class="text-[rgba(148,162,209,1)] text-sm font-medium !mb-5 leading-none">Join</h5>

                        <Link :href="`/pro`" class="block text-[rgba(233,232,255,1)] leading-none font-medium whitespace-nowrap">
                            Pricing
                        </Link>
                        <Link :href="`/testimonials`" class="block text-[rgba(233,232,255,1)] leading-none font-medium whitespace-nowrap">
                            Testimonials
                        </Link>
                    </div>
                </div>
            </div>
            <div class="flex flex-wrap items-baseline justify-between py-5">
                <div class="space-x-3 md:space-x-8 mb-4 mr-6 flex items-center flex-wrap">
                    <Link :href="`/privacy`" class="text-[rgba(112,120,176,1)] text-sm">Privacy</Link>
                    <Link :href="`/terms`" class="text-[rgba(112,120,176,1)] text-sm text-nowrap">Terms</Link>
                    <a target="_blank" href="https://status.codecourse.com" class="text-[rgba(112,120,176,1)] text-sm">Status</a>
                    <Link :href="`/articles/what-codecourse-uses`" class="text-[rgba(112,120,176,1)] text-sm">Uses</Link>
                </div>
                <p class="text-[rgba(112,120,176,1)] text-sm">
                    © 2024 Codecourse Ltd. All Rights Reserved. Made in the UK.
                </p>
            </div>
        </footer>
    </div>
</template>
